import { Injectable, inject, resource, computed } from '@angular/core';
import { collection, query, where, getDocs, doc, getDoc, serverTimestamp, writeBatch, WriteBatch, deleteField, FieldValue } from 'firebase/firestore';
import { FIRESTORE_TOKEN } from '../firebase/firestore.provider';
import { AuthService } from '../auth/auth.service';
import { AccessLogService } from './access-log.service';
import type { Sensor } from '../../shared/interfaces';

@Injectable({
  providedIn: 'root'
})
export class SensorManageService {
  private readonly firestore = inject(FIRESTORE_TOKEN);
  private readonly authService = inject(AuthService);
  private readonly accessLogService = inject(AccessLogService);

  private async logAction(action: string, resourcePath: string, status: 'success' | 'error'): Promise<void> {
    const user = this.authService.user();
    if (!user) return;
    await this.accessLogService.addLog({
      userId: user.uid,
      userName: user.email || '',
      action,
      resource: resourcePath,
      status
    });
  }

  private readonly sensorsResource = resource({
    params: () => ({
      orgId: this.authService.userOrganizationId(),
      role: this.authService.userRole()
    }),
    loader: async ({ params: { orgId, role } }) => {
      if (!orgId && role !== 'super_admin') return [];

      try {
        const sensorsRef = collection(this.firestore, 'sensors');
        let sensorsQuery;
        if (role === 'super_admin') {
          sensorsQuery = query(
            sensorsRef,
            where('deletedAt', '==', null)
          );
        } else {
          sensorsQuery = query(
            sensorsRef,
            where('organizationId', '==', orgId),
            where('deletedAt', '==', null)
          );
        }
        
        const snapshot = await getDocs(sensorsQuery);
        return snapshot.docs
          .map(d => {
            const data = d.data();
            return {
              id: d.id,
              ...data,
              siteId: data['siteId'] ?? null
            } as Sensor;
          });
      } catch (err) {
        console.error('組織内センサーの取得に失敗しました:', err);
        throw new Error('組織内センサーの取得に失敗しました', { cause: err });
      }
    }
  });

  public readonly allSensors = computed(() => this.sensorsResource.value() ?? []);
  
  public readonly isLoading = computed(() => 
    this.sensorsResource.status() === 'loading' || 
    this.sensorsResource.status() === 'reloading'
  );

  public readonly error = computed(() => {
    const err = this.sensorsResource.error();
    if (err) return err instanceof Error ? err.message : '組織内センサーの取得エラー';
    return null;
  });

  private async ensureLatestSiteData(siteId: string, batch: WriteBatch): Promise<void> {
    const latestRef = doc(this.firestore, 'latest_site_data', siteId);
    const latestSnap = await getDoc(latestRef);
    if (!latestSnap.exists()) {
      const siteRef = doc(this.firestore, 'sites', siteId);
      const siteSnap = await getDoc(siteRef);
      if (siteSnap.exists()) {
        const organizationId = siteSnap.data()['organizationId'];
        batch.set(latestRef, {
          id: siteId,
          organizationId,
          deletedAt: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
    }
  }

  private createPositionNamePayload(positionName: string | undefined): { positionName: string | FieldValue } {
    const trimmed = positionName?.trim() ?? '';
    return {
      positionName: trimmed ? trimmed : deleteField()
    };
  }

  public async assignSensor(sensorId: string, siteId: string, positionName?: string): Promise<void> {
    try {
      const sensorRef = doc(this.firestore, 'sensors', sensorId);
      const docSnap = await getDoc(sensorRef);
      if (!docSnap.exists()) throw new Error('センサーが見つかりません');

      const batch = writeBatch(this.firestore);

      const payload: {
        siteId: string;
        positionName: string | FieldValue;
        updatedAt: FieldValue;
        deletedAt?: null;
      } = {
        siteId,
        ...this.createPositionNamePayload(positionName),
        updatedAt: serverTimestamp()
      };
      if (docSnap.data()['deletedAt'] === undefined) {
        payload.deletedAt = null;
      }

      batch.update(sensorRef, payload);
      await this.ensureLatestSiteData(siteId, batch);
      await batch.commit();

      await this.logAction('sensor.assign', `sensors/${sensorId}`, 'success');
      this.sensorsResource.reload();
    } catch (err) {
      console.error('センサーの紐付けに失敗しました:', err);
      await this.logAction('sensor.assign', `sensors/${sensorId}`, 'error');
      throw new Error('センサーの紐付けに失敗しました', { cause: err });
    }
  }

  public async unassignSensor(sensorId: string): Promise<void> {
    try {
      const sensorRef = doc(this.firestore, 'sensors', sensorId);
      const docSnap = await getDoc(sensorRef);
      if (!docSnap.exists()) throw new Error('センサーが見つかりません');

      const oldSiteId = docSnap.data()['siteId'];

      const batch = writeBatch(this.firestore);

      const payload: {
        siteId: null;
        positionName: FieldValue;
        updatedAt: FieldValue;
        deletedAt?: null;
      } = {
        siteId: null,
        positionName: deleteField(),
        updatedAt: serverTimestamp()
      };
      if (docSnap.data()['deletedAt'] === undefined) {
        payload.deletedAt = null;
      }

      batch.update(sensorRef, payload);
      if (oldSiteId) {
        await this.ensureLatestSiteData(oldSiteId, batch);
      }
      await batch.commit();

      await this.logAction('sensor.unassign', `sensors/${sensorId}`, 'success');
      this.sensorsResource.reload();
    } catch (err) {
      console.error('センサーの紐付け解除に失敗しました:', err);
      await this.logAction('sensor.unassign', `sensors/${sensorId}`, 'error');
      throw new Error('センサーの紐付け解除に失敗しました', { cause: err });
    }
  }

  public async updatePositionName(sensorId: string, positionName: string): Promise<void> {
    try {
      const sensorRef = doc(this.firestore, 'sensors', sensorId);
      const docSnap = await getDoc(sensorRef);
      if (!docSnap.exists()) throw new Error('センサーが見つかりません');

      const sensorData = docSnap.data();
      const siteId = sensorData['siteId'];
      const batch = writeBatch(this.firestore);

      const payload: {
        positionName: string | FieldValue;
        updatedAt: FieldValue;
        deletedAt?: null;
      } = {
        ...this.createPositionNamePayload(positionName),
        updatedAt: serverTimestamp()
      };
      if (sensorData['deletedAt'] === undefined) {
        payload.deletedAt = null;
      }

      batch.update(sensorRef, payload);
      if (siteId) {
        await this.ensureLatestSiteData(siteId, batch);
      }
      await batch.commit();

      await this.logAction('sensor.update_position_name', `sensors/${sensorId}`, 'success');
      this.sensorsResource.reload();
    } catch (err) {
      console.error('センサー設置場所の更新に失敗しました:', err);
      await this.logAction('sensor.update_position_name', `sensors/${sensorId}`, 'error');
      throw new Error('センサー設置場所の更新に失敗しました', { cause: err });
    }
  }

  public async updateDrawingPositions(updates: { sensorId: string; position: { x: number; y: number } | null }[]): Promise<void> {
    const BATCH_LIMIT = 400;
    try {
      const sensors = this.allSensors();
      for (let i = 0; i < updates.length; i += BATCH_LIMIT) {
        const chunk = updates.slice(i, i + BATCH_LIMIT);
        const batch = writeBatch(this.firestore);
        for (const update of chunk) {
          const sensorRef = doc(this.firestore, 'sensors', update.sensorId);
          const payload: any = {
            drawingPosition: update.position,
            updatedAt: serverTimestamp()
          };
          const existingSensor = sensors.find(s => s.id === update.sensorId);
          if (existingSensor && existingSensor.deletedAt === undefined) {
            payload.deletedAt = null;
          }
          batch.update(sensorRef, payload);
        }
        await batch.commit();
      }
      this.sensorsResource.reload();
    } catch (err) {
      console.error('センサーの配置位置更新に失敗しました:', err);
      throw new Error('センサーの配置位置更新に失敗しました', { cause: err });
    }
  }
}
